import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import styles from './ReactivatePilotDialog.module.scss'

import { isApiError } from '@/api'
import { EnterprisePilotItem } from '@/api/generated'
import { reactivateEnterprisePilot } from '@/api/internal'
import Button from '@/components/Button'
import DialogWrapper from '@/components/Dialog'
import { useToast } from '@/components/Toast/ToastContext'
import { formatPilotDateLocale, getDefaultPilotEndDate, getMinPilotEndDate } from '@/pages/app/Pilots/utils'

interface ReactivatePilotDialogProps {
  isOpen: boolean
  pilot: EnterprisePilotItem | null
  onClose: () => void
}

const ReactivatePilotDialog = ({ isOpen, pilot, onClose }: ReactivatePilotDialogProps) => {
  const { updateToast } = useToast()
  const queryClient = useQueryClient()

  const [newTrialEnd, setNewTrialEnd] = useState<string>('')

  useEffect(() => {
    if (isOpen && pilot) {
      setNewTrialEnd(getDefaultPilotEndDate())
    }
  }, [isOpen, pilot])

  // Handle edge case: dialog opened without a pilot selected
  useEffect(() => {
    if (isOpen && !pilot) {
      updateToast('No pilot selected', 'error')
      onClose()
    }
  }, [isOpen, pilot, updateToast, onClose])

  const mutation = useMutation({
    mutationFn: async ({ enterpriseId, trialEnd }: { enterpriseId: string; trialEnd: string }) => {
      return reactivateEnterprisePilot(enterpriseId, trialEnd)
    },
    onSuccess: () => {
      updateToast('Pilot reactivated', 'success')
      handleClose()
      // The pilots list reads from a replica, so wait briefly for this write to replicate.
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['enterprise-pilots'] })
      }, 1000)
    },
    onError: (error) => {
      if (isApiError(error)) {
        updateToast(error.detail || error.message || 'Failed to reactivate pilot', 'error')
      } else {
        updateToast('Failed to reactivate pilot', 'error')
      }
    },
  })

  const handleClose = () => {
    setNewTrialEnd('')
    onClose()
  }

  const handleSubmit = () => {
    if (!pilot || !newTrialEnd) return
    mutation.mutate({
      enterpriseId: pilot.enterprise_id,
      trialEnd: newTrialEnd,
    })
  }

  if (!pilot) return null

  return (
    <DialogWrapper isOpen={isOpen} title="Reactivate Pilot" close={handleClose} minWidth={460}>
      <div className={styles.dialogBody}>
        <div className={styles.pilotInfo}>
          <p>
            <strong>Enterprise:</strong> {pilot.enterprise_name}
          </p>
          <p>
            <strong>Original pilot ended:</strong> {formatPilotDateLocale(pilot.pilot_end)}
          </p>
        </div>

        <div className={styles.formField}>
          <label htmlFor="reactivate-trial-end">New Trial End Date</label>
          <input
            id="reactivate-trial-end"
            type="date"
            value={newTrialEnd}
            onChange={(e) => setNewTrialEnd(e.target.value)}
            min={getMinPilotEndDate()}
            className={styles.dateInput}
          />
          <span className={styles.hint}>
            Date is interpreted as end of day UTC. Must be at least 24 hours in the future.
          </span>
        </div>

        <div className={styles.warningMessage}>
          <span className={styles.warningIcon} aria-hidden="true">
            ⚠️
          </span>
          <span>
            Members get Enterprise access again until the new end date, unless you extend or convert the pilot first.
            Nothing is created in Stripe.
          </span>
        </div>

        <div className={styles.dialogFooter}>
          <Button title="Cancel" onClick={handleClose} type="light" isDisabled={mutation.isPending} />
          <Button
            title={mutation.isPending ? 'Reactivating...' : 'Reactivate Pilot'}
            onClick={handleSubmit}
            type="dark"
            isDisabled={mutation.isPending || !newTrialEnd}
          />
        </div>
      </div>
    </DialogWrapper>
  )
}

export default ReactivatePilotDialog
